import { FLEETS, type Fleet, type FleetKind, type FleetStatus } from './fleetData';

export interface Device {
  id: string;
  alias: string;
  name: string;
  fleet: string;
  applicationStatus: 'Healthy' | 'Degraded' | 'Error' | 'Unknown';
  deviceStatus: 'Online' | 'Suspended' | 'Pending Sync' | 'Error';
  systemUpdateStatus: 'Up-to-date' | 'Updating' | 'Out-of-date' | 'Unknown';
  created?: string;
  lastSeen?: string;
  imageName?: string;
  imageVersion?: string;
  imageBuildId?: string;
  osVersion?: string;
  architecture?: string;
  region?: string;
  ipAddress?: string;
  macAddress?: string;
}

const FLEET_REGION: Record<FleetKind, string> = {
  fitting: 'eu-west-1',
  warehouse: 'us-east-1',
  store: 'eu-west-1',
  office: 'us-east-1',
  kiosk: 'eu-central-1',
  plant: 'us-central-1',
  uas: 'eu-central-1',
  billing: 'us-east-1',
  popup: 'us-east-1',
};

const ROLES: Record<FleetKind, string[]> = {
  fitting: ['mirror', 'scanner', 'tablet', 'cam'],
  warehouse: ['recv', 'pick', 'dock', 'conveyor', 'yard'],
  store: ['pos', 'backroom', 'cam', 'inventory'],
  office: ['conf', 'print', 'badge', 'wifi'],
  kiosk: ['kiosk', 'pay', 'wayfind'],
  plant: ['plc', 'cell', 'qa', 'line'],
  uas: ['gcs', 'radio', 'satcom', 'ops'],
  billing: ['edge', 'auth', 'settle', 'queue'],
  popup: ['pos', 'stock', 'cam'],
};

const NAMED_ALIASES: Record<string, string[]> = {
  'Fitting Room': ['Madrid', 'Barcelona', 'Málaga'],
  Store: ['Valencia', 'Córdoba', 'Toledo'],
  Office: ['Sevilla', 'Granada', 'Zaragoza'],
  Warehouse: ['Bilbao', 'Alicante'],
};

type DeviceState = Pick<Device, 'applicationStatus' | 'deviceStatus' | 'systemUpdateStatus'>;

const FLEET_DEVICE_STATES: Record<FleetStatus, DeviceState[]> = {
  Successful: [
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Up-to-date' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Up-to-date' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Up-to-date' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Updating' },
    { applicationStatus: 'Degraded', deviceStatus: 'Suspended', systemUpdateStatus: 'Out-of-date' },
  ],
  Warning: [
    { applicationStatus: 'Degraded', deviceStatus: 'Suspended', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Degraded', deviceStatus: 'Online', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Up-to-date' },
    { applicationStatus: 'Unknown', deviceStatus: 'Pending Sync', systemUpdateStatus: 'Unknown' },
  ],
  Running: [
    { applicationStatus: 'Healthy', deviceStatus: 'Pending Sync', systemUpdateStatus: 'Updating' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Updating' },
    { applicationStatus: 'Healthy', deviceStatus: 'Pending Sync', systemUpdateStatus: 'Updating' },
    { applicationStatus: 'Unknown', deviceStatus: 'Pending Sync', systemUpdateStatus: 'Unknown' },
    { applicationStatus: 'Healthy', deviceStatus: 'Online', systemUpdateStatus: 'Up-to-date' },
  ],
  Failed: [
    { applicationStatus: 'Error', deviceStatus: 'Error', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Error', deviceStatus: 'Suspended', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Error', deviceStatus: 'Error', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Degraded', deviceStatus: 'Suspended', systemUpdateStatus: 'Out-of-date' },
    { applicationStatus: 'Unknown', deviceStatus: 'Error', systemUpdateStatus: 'Unknown' },
  ],
};

function slug(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function pad(value: number, width = 3): string {
  return String(value).padStart(width, '0');
}

function hexByte(value: number): string {
  return (value & 255).toString(16).padStart(2, '0').toUpperCase();
}

function octet(id: number, shift: number): number {
  return (Math.floor(id / shift) % 254) + 1;
}

function siteToken(name: string): string {
  const parts = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[\s\-']+/)
    .filter(Boolean);
  const skip = new Set([
    'the',
    'dc',
    'hq',
    'ems',
    'smt',
    'edge',
    'ground',
    'drone',
    'eu',
    'na',
    'apac',
    'room',
    'mall',
    'street',
    'centre',
    'center',
    'for',
  ]);
  const keep = parts.filter((part) => !skip.has(part.toLowerCase()));
  return slug((keep.slice(0, 2).join(' ') || parts[0] || name).toLowerCase());
}

function deviceAlias(fleet: Fleet, index: number): string {
  const named = NAMED_ALIASES[fleet.name]?.[index];
  if (named) {
    return named;
  }
  const roles = ROLES[fleet.kind];
  const role = roles[index % roles.length];
  return `${siteToken(fleet.name)}-${role}-${pad(index + 1)}`;
}

function deviceHost(alias: string, fleet: Fleet, index: number): string {
  if (NAMED_ALIASES[fleet.name]?.[index]) {
    return `${slug(alias)}.edge.example.com`;
  }
  return `${slug(alias)}.${slug(fleet.name)}.edge.example.com`;
}

function stateFor(fleet: Fleet, index: number): DeviceState {
  const options = FLEET_DEVICE_STATES[fleet.status];
  return options[index % options.length];
}

function imageVersion(fleet: Fleet, index: number): { version: string; buildId: string } {
  if (fleet.status === 'Failed') {
    return { version: 'v1.0.4', buildId: 'imagebuild-031108' };
  }
  if (fleet.status === 'Warning' && index % 5 !== 3) {
    return { version: 'v1.1.5', buildId: 'imagebuild-042156' };
  }
  if (fleet.status === 'Running' && index % 5 !== 4) {
    return { version: 'v1.2.1-rc2', buildId: 'imagebuild-061440' };
  }
  return { version: 'v1.2.0', buildId: 'imagebuild-051982' };
}

function makeDevice(id: number, fleet: Fleet, index: number): Device {
  const alias = deviceAlias(fleet, index);
  const state = stateFor(fleet, index);
  const image = imageVersion(fleet, index);
  const arch = fleet.kind === 'kiosk' || fleet.kind === 'uas' || index % 11 === 0 ? 'aarch64' : 'x86_64';

  return {
    id: String(id),
    alias,
    name: deviceHost(alias, fleet, index),
    fleet: fleet.name,
    applicationStatus: state.applicationStatus,
    deviceStatus: state.deviceStatus,
    systemUpdateStatus: state.systemUpdateStatus,
    created: '2025-01-15T10:30:00Z',
    lastSeen: fleet.status === 'Failed' ? '2026-08-21T09:12:00Z' : '2026-08-29T16:40:00Z',
    imageName: fleet.systemImage,
    imageVersion: image.version,
    imageBuildId: image.buildId,
    osVersion: fleet.systemImage,
    architecture: arch,
    region: FLEET_REGION[fleet.kind],
    ipAddress: `10.${octet(id, 256 * 256)}.${octet(id, 256)}.${octet(id, 1)}`,
    macAddress: `00:1B:44:${hexByte(id >> 16)}:${hexByte(id >> 8)}:${hexByte(id)}`,
  };
}

function buildDevices(): Device[] {
  const devices: Device[] = [];
  let id = 1;
  for (const fleet of FLEETS) {
    for (let index = 0; index < fleet.deviceCount; index += 1) {
      devices.push(makeDevice(id, fleet, index));
      id += 1;
    }
  }
  return devices;
}

let cachedDevices: Device[] | null = null;

export function getMockDevices(): Device[] {
  if (!cachedDevices) {
    cachedDevices = buildDevices();
  }
  return cachedDevices;
}

export const getDeviceById = (id: string): Device | undefined => getMockDevices().find((device) => device.id === id);
